import React, {useEffect, useRef} from "react";
import PropTypes from 'prop-types';

import {flowManager} from "../managers/FlowManager";
import {dataManager} from "../managers/DataManager";
import {dataKeys} from "../enums/dataKeys";
import {useArticles} from "../hooks/useArticles";
import {StepHeader} from "../components/StepHeader";
import {ArticleList} from "../components/ArticleList";
import {AskTheCommunityStep} from "./AskTheCommunityStep";
import {ManageAccountStep} from "./ManageAccountStep";
import {ResolutionList} from "../components/ResolutionList";

KnowledgeBaseStep.propTypes = {
  data: PropTypes.array,
  index: PropTypes.number
}

export function KnowledgeBaseStep(props) {
  const name = KnowledgeBaseStep.name;
  const stepRef = useRef(null);
  const steps = flowManager.store(state => state.steps); // Steps is updated on state change.
  const [articles, isLoading, setUpdateArticles] = useArticles();
  const nextStep = steps[props.index]?.next?.Component?.name;
  const resolutionLinks = dataManager
    .getValuesByKey(dataKeys.resolutionLinks)
    .flat()
    .filter(Boolean);

  // On mount, focus the step and scroll into view.
  useEffect(() => {
    stepRef.current.focus({ preventScroll: true });
    window.scrollTo({
      top: (stepRef.current.offsetTop - 120),
      behavior: "smooth"
    });
  }, []);

  useEffect(() => {
    setUpdateArticles(true);
  }, [setUpdateArticles]);

  // Auto-skip when no articles and no resolution links are found.
  useEffect(() => {
    if (!isLoading && articles.length === 0 && resolutionLinks.length === 0) {
      flowManager.skipStep(props.index);
    }
  }, [isLoading, articles, resolutionLinks.length, props.index]);

  function handleClick() {
    const action = "next";
    const entry = { name, action };
    flowManager.nextStep(props.index, entry);
  }

  if (isLoading) {
    return (
      <div ref={stepRef} tabIndex={0} className="support-form-step support-form-knowledge-base-step-loading">
        <div className="support-form-knowledge-base-step-loading__header">
          <div className="support-form-knowledge-base-step-loading__icon-circle">
            <div className="support-form-knowledge-base-step-loading__icon"/>
          </div>
          <div className="support-form-knowledge-base-step-loading__side-line"/>
        </div>
      </div>
    );
  }

  return (
    <div ref={stepRef} tabIndex={0} className="support-form-step support-form-knowledge-base-step">
      <StepHeader
        index={props.index}
        question="This might help you."
        description={resolutionLinks.length > 0 ? "These links might guide you to your answer." : "These articles may answer your question."}
      />
      {resolutionLinks.length > 0 && (
        <ResolutionList resolutionLinks={resolutionLinks}/>
      )}
      <ArticleList articles={articles} isLoading={isLoading}/>
      {nextStep && steps.length <= props.index + 1 && (
        <React.Fragment>
          <h3 className="support-form-knowledge-base-step__subtitle">Still not found what you were looking for?</h3>
          {nextStep === AskTheCommunityStep.name ? (
            <AskTheCommunityStep nested/>
          ) : nextStep === ManageAccountStep.name ? (
            <ManageAccountStep nested/>
          ) : (
            <React.Fragment>
              <p className="support-form-knowledge-base-step__text">Please feel free to reach out to us. We are
                happy to help!</p>
              <button type="button" className="support-form-knowledge-base-step__button" onClick={handleClick}>
                <span>Contact Support</span>
                <span className="mask-arrow-right"/>
              </button>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </div>
  );
}
